import * as R from 'ramda';
import { capitalizeFirstLetter } from 'products/utils';
import { Product } from '@inter-ikea-storageone/range';
import { ProductDesign } from '@inter-ikea-storageone/range';
import { OptionType, Depth, Colour, OptionValue } from '../types';
import { getRange } from 'utils/queryUtils';

const getDepths = (frameProducts: Product[]): Depth[] => {
  const depths = frameProducts.map(product => {
    return {
      value: product.communicatedDimensions.depth,
      text:
        product.communicatedDimensionsLocalized.depth +
        ' ' +
        product.communicatedDimensionsLocalized.unit
    };
  });
  return R.uniq(depths);
};

const getColours = (frameProduct: Product[]): Colour[] => {
  const colours = frameProduct.map(product => {
    return {
      value: product.design as ProductDesign,
      text: capitalizeFirstLetter(product.designText)
    };
  });
  return R.uniq(colours);
};

const getMatchingFrames = (
  type: OptionType,
  invalidFrameProductsInScene: Product[],
  allAvailableFrameProducts: Product[]
): Product[] => {
  let matchingFrames: Product[] = [];
  let valid = true;

  invalidFrameProductsInScene.forEach(invalidFrame => {
    const frames = allAvailableFrameProducts.filter(p => {
      switch (type) {
        case OptionType.changeColour:
          const invalidColours = getColours(invalidFrameProductsInScene);
          return (
            !invalidColours.map(c => c.value).includes(p.design as ProductDesign) &&
            p.communicatedDimensions.width === invalidFrame.communicatedDimensions.width &&
            p.communicatedDimensions.height === invalidFrame.communicatedDimensions.height &&
            p.communicatedDimensions.depth === invalidFrame.communicatedDimensions.depth
          );
        case OptionType.changeDepth:
          const invalidDepths = getDepths(invalidFrameProductsInScene);
          return (
            !invalidDepths.map(d => d.value).includes(p.communicatedDimensions.depth) &&
            p.communicatedDimensions.width === invalidFrame.communicatedDimensions.width &&
            p.communicatedDimensions.height === invalidFrame.communicatedDimensions.height &&
            p.design === invalidFrame.design
          );
        default:
          return (
            p.communicatedDimensions.width === invalidFrame.communicatedDimensions.width &&
            p.communicatedDimensions.height === invalidFrame.communicatedDimensions.height
          );
      }
    });
    if (frames.length === 0) {
      valid = false;
      return;
    } else {
      matchingFrames = matchingFrames.concat(frames);
    }
  });

  return valid ? matchingFrames : [];
};

export const getSelectedOptions = (
  invalidFramesInScene: boolean,
  options: OptionValue[],
  replacedFrameProducts: Product[]
): number[] => {
  const selectedOption: number[] = [];

  if (invalidFramesInScene) {
    options.forEach((option, index) => {
      if (!option.available) {
        selectedOption.push(index);
      }
    });
  } else {
    const colorsInScene = getColours(replacedFrameProducts).map(c => c.value);
    const depthsInScene = getDepths(replacedFrameProducts).map(d => d.value);

    options.forEach((option, index) => {
      if (option.colour && option.depth) {
        if (
          colorsInScene.includes(option.colour.value) &&
          depthsInScene.includes(option.depth.value)
        ) {
          selectedOption.push(index);
          return;
        }
      } else if (option.colour) {
        if (colorsInScene.includes(option.colour.value)) {
          selectedOption.push(index);
          return;
        }
      } else if (option.depth) {
        if (depthsInScene.includes(option.depth.value)) {
          selectedOption.push(index);
          return;
        }
      }
    });
  }

  return selectedOption;
};

export const checkOption = (
  type: OptionType,
  invalidFrameProductsInScene: Product[],
  allAvailableFrameProducts: Product[]
): boolean => {
  // For BESTÅ changeColour is the only valid option
  if (type !== OptionType.changeColour && getRange() !== 'pax') return false;
  return getMatchingFrames(type, invalidFrameProductsInScene, allAvailableFrameProducts).length > 0;
};

export const getOptions = (
  type: OptionType,
  invalidFrameProductsInScene: Product[],
  allAvailableFrameProducts: Product[]
): OptionValue[] => {
  const options: OptionValue[] = [];

  const matchingFrames = getMatchingFrames(
    type,
    invalidFrameProductsInScene,
    allAvailableFrameProducts
  );
  const invalidDepths = getDepths(invalidFrameProductsInScene);
  const invalidColours = getColours(invalidFrameProductsInScene);
  const availableDepths = getDepths(matchingFrames);
  const availableColours = getColours(matchingFrames);

  switch (type) {
    case OptionType.changeColour:
      availableColours.forEach(colour => {
        options.push({ colour: colour, available: true });
      });
      invalidColours.forEach(colour => {
        options.push({ colour: colour, available: false });
      });
      break;
    case OptionType.changeDepth:
      availableDepths.forEach(depth => {
        options.push({ depth: depth, available: true });
      });
      invalidDepths.forEach(depth => {
        options.push({ depth: depth, available: false });
      });
      break;
    case OptionType.changeDepthAndColour:
      availableDepths.forEach(depth => {
        availableColours.forEach(colour => {
          options.push({
            depth: depth,
            colour: colour,
            available: true
          });
        });
      });
      invalidDepths.forEach(depth => {
        invalidColours.forEach(colour => {
          options.push({
            depth: depth,
            colour: colour,
            available: false
          });
        });
      });
  }

  return options;
};

export const getDescription = (
  type: OptionType,
  success: boolean,
  formatMessage: ({ id }: { id: string }) => string
): string => {
  if (success) {
    return formatMessage({ id: 'storageone.unavailableFramesMenuSubtitleApproved' });
  }
  switch (type) {
    case OptionType.changeColour:
      return formatMessage({ id: 'storageone.unavailableFramesMenuSubtitleUnapprovedColour' });
    case OptionType.changeDepth:
      return formatMessage({ id: 'storageone.unavailableFramesMenuSubtitleUnapprovedSizes' });
    case OptionType.changeDepthAndColour:
      return formatMessage({
        id: 'storageone.unavailableFramesMenuSubtitleUnapprovedSizesColour'
      });
  }
};

export const getListHeading = (
  type: OptionType,
  formatMessage: ({ id }: { id: string }) => string
): string => {
  switch (type) {
    case OptionType.changeColour:
      return formatMessage({ id: 'storageone.unavailableFramesMenuOptionsTitleColour' });
    case OptionType.changeDepth:
      return formatMessage({ id: 'storageone.unavailableFramesMenuOptionsTitleDepth' });
    case OptionType.changeDepthAndColour:
      return formatMessage({ id: 'storageone.unavailableFramesMenuOptionsTitleColourDepth' });
  }
};

export const getLabel = (
  type: OptionType,
  option: OptionValue,
  formatMessage: ({ id }: { id: string }, { depth }: { depth: string }) => string
): string | undefined => {
  if (type === OptionType.changeDepth) {
    return (
      option.depth?.text &&
      formatMessage(
        { id: 'storageone.unavailableFramesMenuOptionsDepthText' },
        { depth: option.depth?.text }
      )
    );
  }
  return option.colour?.text;
};

export const getCaption = (
  type: OptionType,
  option: OptionValue,
  formatMessage: ({ id }: { id: string }, { depth }: { depth: string }) => string
): string | undefined => {
  if (type === OptionType.changeDepthAndColour) {
    return (
      option.depth?.text &&
      formatMessage(
        { id: 'storageone.unavailableFramesMenuOptionsDepthText' },
        { depth: option.depth?.text }
      )
    );
  }
  return undefined;
};
